import { ReactComponent as IconAccountSetting } from "@/assets/svgs/account-setting.svg";
import { ReactComponent as IconNotificationSetting } from "@/assets/svgs/notification-setting.svg";
import Tabs, { Tab } from "@/components/Tabs";
import usePage from "@/hooks/use-page/index.js";
import { useState, useEffect, useMemo } from "react";
import { AccountSetting } from "./components/account-setting.jsx";
import FullAccountSetting from "./components/account-setting-full.jsx";
import { NotificationSetting } from "./components/notification-setting.jsx";
import { AdminNotificationSetting } from "./components/admin-notification-setting.jsx";
import ModelTermForm from "./components/model-term-form.jsx";
import useModelTerm from "@/hooks/use-term-template/index.js";
import { useFeature } from "@/hooks/use-feature/index.js";
import { featureConstant } from "@/constants/configuration/feature.js";
import { NavLink, useNavigate, useLocation } from "@/lib/router-dom/index.js";
import Breadcrumbs from "@/componentsDS/Breadcrumbs/index.jsx";

const SHELL_HOSTED = process.env.REACT_APP_SHELL_HOSTED === 'true';

const Page = ({ setLoading }) => {
	const { currentPath } = useLocation();
	const { replace } = useNavigate();

	const [createModelTerm, setCreateModelTerm] = useState(false);
	const { fetchModelTermList, modelTermList } = useModelTerm();
	const useCases = sessionStorage.getItem("useCases");
	const isIGPNILegalRequirementsEnabled = useFeature(featureConstant.IG_PNI_LEGAL_REQUIREMENTS);
	// Unified portal: the admin notifications tab exists only when this session
	// carries an admin token.
	const hasAdmin = !!sessionStorage.getItem("adminCoreToken");

	const pageTitle = "Account Settings";

	const defaultBreadCrumbs = {
		title: pageTitle,
		breadcrumbs: [
			{ label: '', link: `/` },
			{ label: 'Account Settings', link: `/setting/account` },
		],
	};

	useEffect(() => {
		if (currentPath === "/setting") {
			replace("/setting/account");
		}
	}, []);

	useEffect(() => {
		const newTabItems = tabsItems.map((item) => ({
			...item,
			active: item.path === currentPath,
		}));
		setTabItems(newTabItems);
	}, [currentPath]);

	const modelTermEnabled = useMemo(() => {
		// SHELL_HOSTED first: legacy pitstop-ui must never parse `useCases` (a
		// portal-written key) nor enable the eBL fetch, even on IG_PNI tenants.
		if (SHELL_HOSTED && isIGPNILegalRequirementsEnabled && useCases) {
			const parsedUseCases = JSON.parse(useCases);
			return parsedUseCases.some(
				(useCase) => useCase.enableETR && useCase.etrIssuance?.contracts?.length > 0
			);
		}
		return false;
	}, [useCases]);

	useEffect(() => {
		if (isIGPNILegalRequirementsEnabled && modelTermEnabled) {
			fetchModelTermList();
		}
	}, [modelTermEnabled]);

	const myAccountTab = {
		title: "My Account",
		label: (
			<NavLink className="setting-btn" to="/setting/account">
				<IconAccountSetting />
				<div className="setting-btn-txt font-weight-bold">My Account</div>
			</NavLink>
		),
		path: "/setting/account",
		active: true,
	};

	// The native pitstop notifications tab. Legacy label stays "Notifications";
	// on the unified portal it becomes "Pitstop Notifications".
	const pitstopNotificationTitle = SHELL_HOSTED ? "Pitstop Notifications" : "Notifications";
	const pitstopNotificationTab = {
		title: pitstopNotificationTitle,
		label: (
			<NavLink className="setting-btn" to="/setting/notification">
				<IconNotificationSetting />
				<div className="setting-btn-txt font-weight-bold">{pitstopNotificationTitle}</div>
			</NavLink>
		),
		path: "/setting/notification",
		active: false,
	};

	const adminNotificationTab = {
		title: "Admin Notifications",
		label: (
			<NavLink className="setting-btn" to="/setting/admin-notification">
				<IconNotificationSetting />
				<div className="setting-btn-txt font-weight-bold">Admin Notifications</div>
			</NavLink>
		),
		path: "/setting/admin-notification",
		active: false,
	};

	const defaultTabItems = SHELL_HOSTED
		? [myAccountTab, pitstopNotificationTab, ...(hasAdmin ? [adminNotificationTab] : [])]
		: [myAccountTab, pitstopNotificationTab];

	const [tabsItems, setTabItems] = useState(
		defaultTabItems.map((item) => ({
			...item,
			active: item.path === currentPath,
		}))
	);

	const { breadcrumbsItems } = usePage(defaultBreadCrumbs);

	const selectTab = (item) => {
		const newTabItems = tabsItems.map((value) => {
			value.active = item.path === value.path;
			return value;
		});
		setTabItems(newTabItems);
	};

	// Full-page eBL template form (shell-hosted My Account only) — mirrors admin-ui.
	if (SHELL_HOSTED && isIGPNILegalRequirementsEnabled && createModelTerm) {
		return (
			<ModelTermForm
				setCreateModelTerm={setCreateModelTerm}
				termTemplate={modelTermList[0]}
			/>
		);
	}

	return (
		<>
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />
			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title font-weight-bold">{pageTitle}</div>
				</div>
			</div>

			<div>
				<Tabs>
					{tabsItems.map((item, index) => (
						<Tab
							key={index}
							label={item.label}
							active={item.active}
							onClick={() => selectTab(item)}
						/>
					))}
				</Tabs>
			</div>
			<div className="eclip-hr">
				<hr />
			</div>
			{currentPath === "/setting/account" && (
				SHELL_HOSTED ? (
					<FullAccountSetting
						setLoading={setLoading}
						modelTermEnabled={modelTermEnabled}
						setCreateModelTerm={setCreateModelTerm}
						termTemplate={modelTermList[0]}
					/>
				) : (
					<AccountSetting setLoading={setLoading} />
				)
			)}
			{currentPath === "/setting/notification" && (
				<NotificationSetting setLoading={setLoading} />
			)}
			{SHELL_HOSTED && hasAdmin && currentPath === "/setting/admin-notification" && (
				<AdminNotificationSetting setLoading={setLoading} />
			)}
		</>
	);
};

export default Page;
